import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../../components/feature/HeroSection';
import TrustBar from '../../components/feature/TrustBar';
import ServicesGrid from '../../components/feature/ServicesGrid';
import AIAnswerBlock from '../../components/feature/AIAnswerBlock';
import ProcessSection from '../../components/feature/ProcessSection';
import CTABand from '../../components/feature/CTABand';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import ServiceAreas from '../../components/feature/ServiceAreas';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTA from '../../components/feature/FinalCTA';

const BRAND_STORY = [
  {
    icon: 'ri-focus-2-line',
    title: 'Our Mission',
    description:
      'To deliver precision-engineered commercial LED lighting solutions that meaningfully reduce energy costs, eliminate maintenance burdens, and improve light quality for businesses throughout Tampa Bay — managed with complete accountability under one roof.',
    link: '/services',
  },
  {
    icon: 'ri-medal-line',
    title: 'Our Expertise',
    description:
      'Southeast Lighting & Design is operated by Southeast Green Energy Solutions, LLC — with deep expertise in photometric design, commercial LED products, solar all-in-one systems, and energy savings project management across every commercial application type.',
    link: '/services',
  },
  {
    icon: 'ri-handshake-line',
    title: 'Our Commitment',
    description:
      'We manage every aspect of your commercial lighting project from signed and sealed plans to installation coordination and rebate acquisition. Our clients benefit from a single expert partner fully responsible for their entire project outcome.',
    link: '/services',
  },
];

const AI_QAS = [
  {
    question: 'Who is Southeast Lighting & Design?',
    answer:
      'Southeast Lighting & Design is a full-service commercial LED lighting company based in Tampa Bay, Florida — operated by Southeast Green Energy Solutions, LLC. We serve commercial and industrial clients throughout Florida, with experience completing LED lighting projects across the country covering virtually every application type.',
  },
  {
    question: 'What is Southeast Lighting\'s approach to commercial projects?',
    answer:
      'We believe every commercial lighting project deserves precision engineering and end-to-end management. That means signed and sealed photometric plans, carefully specified commercial LED products, coordinated installation, and complete rebate acquisition — all under one roof, with no accountability gaps between design, supply, and installation phases.',
  },
  {
    question: 'Why should Tampa Bay businesses choose Southeast Lighting & Design?',
    answer:
      'Because we reduce energy costs by more than 70%, eliminate maintenance cycles, and manage the entire project from design through rebates. We bring commercial LED expertise, solar all-in-one solutions, and proven national project experience to every Tampa Bay client — delivering measurable results for every commercial property type we serve.',
  },
];

const COMPANY_HIGHLIGHTS = [
  {
    icon: 'ri-map-pin-2-line',
    title: 'Tampa Bay Based',
    description:
      'Headquartered in the Tampa Bay area of Florida, serving local commercial clients with responsive service, regional expertise, and direct knowledge of Florida utility rebate programs and local permitting requirements.',
  },
  {
    icon: 'ri-globe-line',
    title: 'Nationwide Project Experience',
    description:
      'Commercial LED lighting projects completed across Florida and throughout the country — bringing national-level expertise and broad application experience to every local Tampa Bay project we take on.',
  },
  {
    icon: 'ri-sun-line',
    title: 'Solar LED Specialists',
    description:
      'One of the few commercial lighting firms in Tampa Bay offering Solar All-In-One LED fixtures — self-contained units requiring zero wiring or electrical trenching, ideal for Florida\'s abundant sunshine and diverse commercial property types.',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Energy Savings Authority',
    description:
      'Demonstrated track record of reducing commercial property energy costs by 70% or more through LED upgrades, photometric optimization, and full-service project management including rebate acquisition across every application type.',
  },
];

const PROJECTS = [
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/photo-gallery/imported_from_media_libray/thumb/VPC-Night-PICS-2-3.jpg',
    imageAlt: 'VPC commercial lighting project at night — Southeast Lighting & Design, Florida',
    name: 'VPC Night Project',
    location: 'Florida',
    type: 'Commercial Site Lighting',
    metric: '70%+',
    metricLabel: 'Energy Reduction',
  },
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/2021/03/Canopy-Oaks-6-2048x1000.jpg',
    imageAlt: 'Solar All-In-One LED lighting installed at Canopy Oaks RV Resort, Central Florida',
    name: 'Canopy Oaks RV Resort',
    location: 'Central Florida',
    type: 'Solar All-In-One Lighting',
    metric: '0',
    metricLabel: 'Wiring Required',
  },
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/2019/01/Safety-Harbor-Tennis-Court-2048x1000.jpg',
    imageAlt: 'LED sports lighting at Safety Harbor Tennis Courts — Safety Harbor, Florida',
    name: 'Safety Harbor Tennis Courts',
    location: 'Safety Harbor, Florida',
    type: 'Sports Court Lighting',
    metric: '70%+',
    metricLabel: 'Energy Saved',
  },
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/2019/02/Alafaya-Square-1-2048x1000.jpg',
    imageAlt: 'LED site lighting at Alafaya Square retail shopping center in Oviedo, Florida',
    name: 'Alafaya Square',
    location: 'Oviedo, Florida',
    type: 'Retail Site Lighting',
    metric: 'LED',
    metricLabel: 'Full Site Retrofit',
  },
];

const AREA_GROUPS = [
  {
    region: 'Pinellas County',
    cities: ['Clearwater', 'St. Petersburg', 'Safety Harbor', 'Dunedin', 'Tarpon Springs', 'Largo'],
  },
  {
    region: 'Hillsborough County',
    cities: ['Tampa', 'Brandon', 'Plant City', 'Lutz', 'Temple Terrace', 'Riverview'],
  },
  {
    region: 'Pasco County',
    cities: ['New Port Richey', 'Land O\' Lakes', 'Wesley Chapel', 'Zephyrhills', 'Dade City'],
  },
  {
    region: 'Central Florida & Beyond',
    cities: ['Oviedo', 'Orlando', 'Seminole County', 'Winter Haven', 'Lake Wales', 'Nationwide'],
  },
];

const FAQ_ITEMS = [
  {
    question: 'How long has Southeast Lighting & Design been in business?',
    answer:
      'Southeast Lighting & Design, operated by Southeast Green Energy Solutions, LLC, has accumulated extensive experience across commercial LED projects throughout Tampa Bay, Florida, and nationwide — serving auto dealerships, retail centers, sports facilities, industrial clients, and more across every major commercial application type.',
  },
  {
    question: 'Does Southeast Lighting handle both design and installation coordination?',
    answer:
      'Yes. We are a full-service firm managing photometric design, product specification, installation coordination with licensed contractors, and utility rebate acquisition. Clients receive a single point of contact managing every phase — no gaps between design, supply, and installation, and no multiple vendors for clients to coordinate independently.',
  },
  {
    question: 'What geographic areas does Southeast Lighting serve?',
    answer:
      'We are based in Tampa Bay and primarily serve commercial clients throughout Pinellas, Hillsborough, Pasco, Seminole, and Polk counties. We also complete commercial lighting projects throughout Florida and have delivered projects nationally. See our Tampa Bay page for specific service area details by county and city.',
  },
  {
    question: 'Can Southeast Lighting help us obtain utility rebates for our LED upgrade?',
    answer:
      'Yes. Rebate acquisition is included in our full-service project management. We handle the rebate application process on your behalf — ensuring you capture all available incentives from your Florida utility provider and maximizing your return on investment by reducing the net upfront cost of your commercial LED upgrade.',
  },
  {
    question: 'Do you provide lighting solutions for existing sites or only new construction?',
    answer:
      'We serve both. We provide signed and sealed photometric lighting plans for new construction and for upgrades to existing commercial sites. Our team assesses current lighting conditions and designs LED solutions optimized for your specific property layout, application type, and local foot-candle code requirements.',
  },
  {
    question: 'What industries and property types has Southeast Lighting served?',
    answer:
      'We have served auto dealerships, retail shopping centers, tennis and pickleball courts, fast food and restaurant facilities, industrial warehouses, agricultural operations, RV resorts, multi-family residential complexes, and municipal recreation facilities — across Tampa Bay, throughout Florida, and in states beyond. Visit our Services page for the full application list.',
  },
];

export default function AboutPage() {
  return (
    <div className="bg-off-white">
      <Navbar />
      <HeroSection
        eyebrow="Southeast Green Energy Solutions, LLC"
        headline="Tampa Bay's Trusted Commercial Lighting Partner."
        subheadline="We are a full-service commercial LED lighting company — managing photometric design, product specification, installation coordination, and rebate acquisition for commercial businesses across Florida and the country."
        ctaText="Get a Free Consultation"
        ctaLink="/location"
        secondaryCta="Call 727-914-9002"
        secondaryCtaLink="tel:7279149002"
        bgImage="https://public.readdy.ai/ai/img_res/964bdef981de22f50f342d6832518326.jpg"
      />
      <TrustBar />
      <ServicesGrid
        eyebrow="Who We Are"
        title="Built on Expertise. Driven by Results."
        subtitle="Southeast Lighting & Design is defined by technical precision, end-to-end project management, and a genuine commitment to measurable commercial energy savings."
        items={BRAND_STORY}
        ctaText="View Our Services"
        ctaLink="/services"
      />
      <AIAnswerBlock
        eyebrow="About Our Company"
        title="Direct Answers About Southeast Lighting & Design"
        qas={AI_QAS}
        image="https://www.selightingdesign.com/wp-content/uploads/2022/02/VPC-Pic-9-scaled-2048x1000.jpg"
        imageAlt="VPC commercial site lighting project — Southeast Lighting & Design, Florida"
      />
      <ProcessSection />
      <CTABand
        text="One partner. One process. From design to rebates — fully managed, start to finish."
        buttonText="Get in Touch"
        buttonLink="/location"
        showPhone
        variant="gold"
      />
      <WhyChooseUs
        eyebrow="Company Highlights"
        headline="Tampa Bay Expertise. Nationwide Experience."
        image="https://readdy.ai/api/search-image?query=nighttime%20aerial%20view%20of%20large%20commercial%20industrial%20facility%20fully%20illuminated%20by%20powerful%20LED%20floodlights%20and%20high%20bay%20lighting%2C%20dramatic%20contrast%20between%20dark%20surrounding%20landscape%20and%20brightly%20lit%20facility%2C%20professional%20commercial%20photography%2C%20premium%20industrial%20real%20estate%2C%20wide%20establishing%20shot%20showing%20full%20property&width=1200&height=800&seq=about-why-2026-v1&orientation=landscape"
        imageAlt="Commercial LED lighting — industrial facility, Florida"
        benefits={COMPANY_HIGHLIGHTS}
        ctaText="Contact Our Team"
        ctaLink="/location"
      />
      <ServiceAreas
        title="Serving Tampa Bay & Throughout Florida"
        subtitle="Headquartered in Tampa Bay with commercial LED project experience across Florida and throughout the country."
        areaGroups={AREA_GROUPS}
      />
      <TestimonialsSection
        eyebrow="Featured Projects"
        title="Real Projects. Measurable Results."
        projects={PROJECTS}
        ctaText="Contact Our Team"
        ctaLink="/location"
      />
      <CTABand
        text="Let's discuss your commercial property's lighting needs — no obligation required."
        buttonText="Contact Our Team"
        buttonLink="/location"
        showPhone
        variant="dark"
      />
      <FAQSection
        title="Questions About Our Company"
        items={FAQ_ITEMS}
        image="https://www.selightingdesign.com/wp-content/uploads/2019/02/Alafaya-Square-1-2048x1000.jpg"
        imageAlt="LED site lighting at Alafaya Square — Southeast Lighting & Design project"
      />
      <FinalCTA
        eyebrow="Let's Work Together"
        headline="Ready to Partner with Tampa Bay's Commercial Lighting Experts?"
        subtext="Reach out to discuss your commercial property's lighting needs. Our team provides complimentary assessments and detailed project proposals for every qualified project."
      />
      <Footer />
    </div>
  );
}
